import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GetOrganizationsResponseDto } from "@workspace/dto/organization/get-organizations";
import type { UserMutationResponseDto } from "@workspace/dto/user/mutation-response";
import type { SwitchOrganizationDto } from "@workspace/dto/user/switch-organization";
import {
  Avatar,
  AvatarImage,
  InitialsAvatarFallback,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { Skeleton } from "@workspace/ui/components/skeleton";
import { cn } from "@workspace/ui/lib/utils";
import type { AxiosResponse } from "axios";
import { CheckIcon, PlusIcon } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import { toast } from "sonner";
import type { ExtendedUser } from "@/auth";
import { Loading } from "@/components/loading";
import { CreateOrganizationModal } from "@/components/modals/organizations/create-organization";
import { usePilotSelfServeLocked } from "@/components/providers/pilot-flag-provider";
import { useSharedTransition } from "@/components/providers/transition-provider";
import { ThemeSwitch } from "@/components/theme-switch";
import { accountPath } from "@/lib/account-navigation";
import { refreshSessionFromDatabase } from "@/lib/auth/session-refresh";
import { useStableSession } from "@/lib/auth/use-stable-session";
import { axios } from "@/lib/axios";
import { getEmployerVisibleOrganizations } from "@/lib/employer-organizations";
import { getOrgSwitchRedirectDestination } from "@/lib/org-switch-navigation";
import { toastMutationNetworkError } from "@/lib/query-failure";
import { queryKeys } from "@/lib/query-keys";
import { SETTINGS_ENTRY_LABELS } from "@/lib/settings-navigation";

const AUTH_PATHS = ["/login", "/logout", "/onboarding"];

type UserAvatarMenuProps = {
  align?: "start" | "center" | "end";
  side?: "top" | "bottom";
  showName?: boolean;
  avatarSize?: "sm" | "md";
  showOrganizations?: boolean;
  onFeedbackSelect?: () => void;
  compact?: boolean;
};

export function UserAvatarMenu({
  align = "end",
  side = "bottom",
  showName = false,
  avatarSize = "md",
  showOrganizations = true,
  onFeedbackSelect,
  compact = false,
}: UserAvatarMenuProps) {
  const router = useRouter();
  const { data: session, status, update } = useStableSession();
  const user = session?.user as ExtendedUser | undefined;
  const { isPending, startTransition } = useSharedTransition();
  const queryClient = useQueryClient();
  const [isCreateOrganizationOpen, setCreateOrganizationOpen] = useState(false);
  const pilotSelfServeLocked = usePilotSelfServeLocked();

  const { data } = useQuery({
    queryKey: queryKeys.account.organizations,
    queryFn: async () => {
      const response = await axios.get<
        GetOrganizationsResponseDto,
        AxiosResponse<GetOrganizationsResponseDto>
      >("/account/get-organizations");
      return response.data;
    },
    enabled:
      showOrganizations &&
      router.isReady &&
      !!user &&
      user?.type === "employer",
  });

  const updateUserMutation = useMutation({
    mutationFn: async (updateData: SwitchOrganizationDto) => {
      const response = await axios.put<
        UserMutationResponseDto,
        AxiosResponse<UserMutationResponseDto>,
        SwitchOrganizationDto
      >("/account/user/switch-organization", updateData);
      return response.data;
    },
    onSuccess: async () => {
      await refreshSessionFromDatabase(update);
      const destination = getOrgSwitchRedirectDestination(router.asPath);
      if (destination) {
        router.replace(destination);
      } else {
        router.reload();
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.account.organizations,
      });
    },
    onError: (error) => {
      if (toastMutationNetworkError(error, "Couldn't change organization")) {
        return;
      }
      toast.error(
        "Uh Oh! Something went wrong. Failed to change organization",
        {
          description: "Failed to change organization",
        }
      );
    },
  });

  if (status === "loading") {
    return <Skeleton className="h-8 w-8 rounded-full" />;
  }

  if (status === "unauthenticated") {
    const isAuthPage = AUTH_PATHS.some((p) => router.pathname.startsWith(p));
    const loginHref = isAuthPage
      ? "/login"
      : `/login?from=${encodeURIComponent(router.asPath)}`;
    return (
      <Button asChild className="text-sm" size="sm" variant="ghost">
        <Link href={loginHref}>Sign in</Link>
      </Button>
    );
  }

  const avatarClasses = avatarSize === "sm" ? "h-7 w-7" : "h-8 w-8";
  const organizations = data?.organizations ?? [];
  const visibleOrganizations = getEmployerVisibleOrganizations(organizations);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(
            "flex items-center gap-2 transition hover:bg-muted",
            compact ? "rounded-full p-0" : "rounded-md p-1"
          )}
        >
          <Avatar className={avatarClasses}>
            <AvatarImage alt={user?.name ?? ""} src={user?.image ?? ""} />
            <InitialsAvatarFallback name={user?.name ?? ""} />
          </Avatar>
          {showName && (
            <span className="ml-2 min-w-0 truncate text-sm">
              {user?.name ?? ""}
            </span>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          className="min-w-[200px]"
          side={side}
        >
          {showOrganizations && user?.type === "employer" && (
            <>
              <DropdownMenuLabel
                className={
                  pilotSelfServeLocked
                    ? undefined
                    : "flex items-center justify-between"
                }
              >
                <span>Organizations</span>
                {!pilotSelfServeLocked && (
                  <Button
                    className="bg-transparent px-1.5 py-1"
                    onClick={() => setCreateOrganizationOpen(true)}
                    onPointerDown={(event) => event.preventDefault()}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </Button>
                )}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {visibleOrganizations.map((org) => (
                <DropdownMenuItem
                  className="flex flex-row items-center gap-x-2"
                  key={org.id}
                  onSelect={() =>
                    updateUserMutation.mutate({
                      organizationId: org.id,
                    })
                  }
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage alt={org.name} src={org.logo_url} />
                    <InitialsAvatarFallback name={org.name} />
                  </Avatar>
                  <span className="min-w-0 truncate">{org.name}</span>
                  {user.organization === org.id ? (
                    <CheckIcon className="ml-auto h-4 w-4" />
                  ) : null}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuItem asChild>
            <Link href={accountPath({ tab: "profile" })}>
              {SETTINGS_ENTRY_LABELS.profile}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href="/support">Support</Link>
          </DropdownMenuItem>
          {onFeedbackSelect && (
            <DropdownMenuItem onSelect={onFeedbackSelect}>
              Feedback
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <div className="flex flex-row items-center justify-between p-2">
            <p className="text-sm">Theme</p>
            <ThemeSwitch />
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={isPending}
            onClick={(e) => {
              e.preventDefault();
              if (startTransition) {
                startTransition(() => {
                  router.push("/logout");
                });
              }
            }}
          >
            {isPending ? <Loading height={18} width={18} /> : "Logout"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {!pilotSelfServeLocked && (
        <CreateOrganizationModal
          isOpen={isCreateOrganizationOpen}
          onOpenChange={setCreateOrganizationOpen}
        />
      )}
    </>
  );
}
